import React from 'react';
import clsx from 'clsx';
import { useLoaderData } from 'react-router-dom';

import { Await } from '@oysterjs/ui/async';
import { Checkbox } from '@oysterjs/ui/checkbox';
import type { ColumnDef, SortDirection } from '@oysterjs/ui/data-table';
import type { ActiveFilter } from '@oysterjs/ui/search-filter';
import { Spinner } from '@oysterjs/ui/spinner';
import { TableHeader } from '@oysterjs/ui/table';

type MutationResult = { errors?: readonly unknown[] | null };

/** A rejected request counts as a refusal, so a network failure still reports. */
export const settle = (request: Promise<MutationResult>): Promise<MutationResult> =>
  request.catch(() => ({ errors: [null] }));

/**
 * One request per row, for the client mutations that take a single row. A
 * rejected request counts as that row failing rather than failing the batch.
 */
export const eachOf = (ids: string[], action: (id: string) => Promise<MutationResult>) =>
  Promise.all(ids.map((id) => settle(action(id))));

/**
 * Runs a row action over a selection, then re-reads the list. A partial failure
 * is reported by count: the rows that went through have already left the list.
 */
export const useBulkAction = (refetch: () => Promise<unknown>) => {
  const [running, setRunning] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const run = React.useCallback(
    async (
      key: string,
      action: () => Promise<MutationResult[]>,
      failure: (failedCount: number) => string
    ): Promise<boolean> => {
      setRunning(key);
      setError(null);
      try {
        const results = await action().catch((): MutationResult[] => [{ errors: [null] }]);
        const failedCount = results.filter((result) => (result.errors?.length ?? 0) > 0).length;
        setError(failedCount > 0 ? failure(failedCount) : null);
        await refetch();
        return failedCount === 0;
      } finally {
        setRunning(null);
      }
    },
    [refetch]
  );

  return { running, error, run, clearError: () => setError(null) };
};

/**
 * Filter and sort for a client tab. Held in component state rather than
 * localStorage like the top-level lists: a filter saved from one client would
 * open the next one already narrowed, and read as that client having less.
 */
export const useClientListControls = <F, S extends string>(defaultSortBy: S) => {
  const [activeFilters, setActiveFilters] = React.useState<ActiveFilter<F>[]>([]);
  const [sort, setSortState] = React.useState<{ sortBy: S; sortDirection: SortDirection }>({
    sortBy: defaultSortBy,
    sortDirection: 'desc'
  });
  const setSort = React.useCallback(
    (sortBy: S, sortDirection: SortDirection) => setSortState({ sortBy, sortDirection }),
    []
  );
  return { activeFilters, setActiveFilters, ...sort, setSort };
};

/** A checkbox column over the rows on screen; the header toggles all of them. */
export const selectionColumn = <T extends { id: string }, S extends string = string>(
  rows: T[],
  selected: ReadonlySet<string>,
  setSelected: React.Dispatch<React.SetStateAction<string[]>>,
  labelOf: (row: T) => string
): ColumnDef<T, S> => ({
  id: 'select',
  noHref: true,
  cellClassName: 'w-0 py-1.5 px-3',
  renderHeader: () => (
    <TableHeader className="w-0 py-1.5 px-3 text-xs">
      <Checkbox
        aria-label="Select all"
        color="sky"
        checked={rows.length > 0 && rows.every((row) => selected.has(row.id))}
        onChange={(checked) => setSelected(checked ? rows.map((row) => row.id) : [])}
      />
    </TableHeader>
  ),
  renderCell: (row) => (
    <Checkbox
      aria-label={`Select ${labelOf(row)}`}
      color="sky"
      checked={selected.has(row.id)}
      onChange={(checked) =>
        setSelected((prev) =>
          checked ? [...new Set([...prev, row.id])] : prev.filter((id) => id !== row.id)
        )
      }
    />
  )
});

/** A card on the Overview, headed the way the analytics cards are. */
export const ClientCard = (props: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <section className="rounded-lg bg-white px-5 py-5 shadow sm:px-6 dark:bg-neutral-800">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="my-0 text-sm font-semibold text-neutral-900 dark:text-white">
          {props.title}
        </h3>
        {props.subtitle && (
          <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">{props.subtitle}</p>
        )}
      </div>
      {props.action}
    </div>
    <div className="mt-4">{props.children}</div>
  </section>
);

export const ClientTabLoader = ({ noun }: { noun: string }) => (
  <div className="rounded-lg bg-white px-6 shadow dark:bg-neutral-800">
    <div className="py-12 text-center">
      <Spinner size="size-8" color="neutral" />
      <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
        Loading {noun}...
      </h3>
      <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
        This might take a moment.
      </p>
    </div>
  </div>
);

type EmptyStateProps = {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  children?: React.ReactNode;
};

/** An empty state without a card of its own, for a list inside one. */
export const ClientEmptyMessage = (props: EmptyStateProps & { className?: string }) => (
  <div className={clsx('text-center', props.className)}>
    <props.icon className="mx-auto size-10 text-neutral-400 dark:text-neutral-500" />
    <h4 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">{props.title}</h4>
    <p className="mx-auto mt-1 max-w-md text-sm text-neutral-500 dark:text-neutral-400">
      {props.description}
    </p>
    {props.children && <div className="mt-4 flex justify-center">{props.children}</div>}
  </div>
);

export const ClientTabEmptyState = (props: EmptyStateProps) => (
  <ClientEmptyMessage
    {...props}
    className="rounded-lg bg-white px-6 py-12 shadow dark:bg-neutral-800"
  />
);

/**
 * Renders a client tab once its route loader resolves, fading it in over the
 * loading card. The loader has written the first page to the cache by then, so
 * the tab's own cache-first query answers without a second request.
 */
export const ClientTabRoute = <T extends Record<string, unknown>>(props: {
  noun: string;
  children: (data: T) => React.ReactNode;
}) => {
  const loader = useLoaderData() as { data: Promise<T> } | undefined;
  return (
    <React.Suspense fallback={<ClientTabLoader noun={props.noun} />}>
      <Await resolve={loader?.data}>{props.children}</Await>
    </React.Suspense>
  );
};
